import { SessionStorageKey } from '@/constants'
import { SignUpEntryPoint, SignUpMethod } from '~/shared/analytics/index.h'
import { type SocialSignUpMarker } from './index.h'

const isSignUpMethod = (value: unknown): value is SignUpMethod =>
  Object.values(SignUpMethod).includes(value as SignUpMethod)

const isSignUpEntryPoint = (value: unknown): value is SignUpEntryPoint =>
  Object.values(SignUpEntryPoint).includes(value as SignUpEntryPoint)

// Session storage throws rather than returns on a blocked store (Safari private
// browsing), and analytics must never take the page down with it.
export const writeSocialSignUpMarker = (marker: SocialSignUpMarker): void => {
  try {
    window.sessionStorage.setItem(
      SessionStorageKey.SocialSignUp,
      JSON.stringify(marker)
    )
  } catch {
    /* the sign_up event is lost, the page is not */
  }
}

export const clearSocialSignUpMarker = (): void => {
  try {
    window.sessionStorage.removeItem(SessionStorageKey.SocialSignUp)
  } catch {
    /* see writeSocialSignUpMarker */
  }
}

/**
 * Reads the marker back, rejecting anything that is not a value this build wrote
 * — the key outlives a deploy, and an unknown string must not reach the event.
 */
export const readSocialSignUpMarker = (): SocialSignUpMarker | null => {
  try {
    const raw = window.sessionStorage.getItem(SessionStorageKey.SocialSignUp)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null

    const { method, entryPoint } = parsed as Record<string, unknown>
    if (!isSignUpMethod(method) || !isSignUpEntryPoint(entryPoint)) return null

    return { method, entryPoint }
  } catch {
    return null
  }
}
